import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, ShieldCheck, ShieldAlert, PenLine, Clock,
  Inbox, Bot, CheckCircle2, Users, Gauge, HandMetal, ClipboardCheck,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { InstitutionInquiryModal } from '../components/brand/InstitutionInquiryModal';

/* ───────────────────────────────────────────────────────────────────────
   For Discharge Planners — rewritten around the planner's DAY (Phase 7):
   the exception queue, the coverage gate, the scoreboard, and automated
   matching with human control at acceptance and signature. Honest copy:
   nothing in test/mock mode is marketed as live.
   ─────────────────────────────────────────────────────────────────── */

const DAY_TILES = [
  {
    icon: ShieldAlert,
    k: 'Exceptions first',
    title: 'Your morning starts with what’s stuck',
    body: 'One queue: coverage holds, expired or declined offers, documents that came back unsigned, escalations. Each card carries the one action that unblocks it.',
  },
  {
    icon: ShieldCheck,
    k: 'The coverage gate',
    title: 'Coverage checked before day one',
    body: 'Eligibility and authorization run inside the loop, before any document routes for signature. A problem pauses the loop and asks a human — it never fails silently.',
  },
  {
    icon: Bot,
    k: 'Automated matching',
    title: 'The machine does the phone tree',
    body: 'Scoring, ranking, and offer waves run themselves. You see every step in a full trace — who was offered, when, and why.',
  },
  {
    icon: PenLine,
    k: 'Orders in one place',
    title: 'F2F, 485/POC, NOA, consent — one thread',
    body: 'The standard order set is drafted automatically once coverage clears, and signed in-platform. No fax loops, no “did they get it?”',
  },
];

const HUMAN_GATES = [
  {
    n: '1',
    title: 'Acceptance',
    body: 'No placement is final until a real clinician says yes. The first credentialed match to accept wins — and you see the confirmation instantly.',
  },
  {
    n: '2',
    title: 'Signature',
    body: 'Nothing goes out under a licensed name without a licensed human signing it. The packet and the order set wait for sign-off, every time.',
  },
  {
    n: '+',
    title: 'Any coverage hold',
    body: 'If eligibility or authorization can’t be verified, the loop stops and asks you. Override with a reason, or reject and escalate — your call, on the record.',
  },
];

const SCOREBOARD = [
  { label: 'Time to accept', body: 'From referral to a confirmed yes — the number your bed meetings run on.' },
  { label: 'Time to first visit', body: 'From acceptance to a committed visit window your team can quote.' },
  { label: 'Exceptions cleared', body: 'Coverage holds and stalls resolved, with the override history to show for it.' },
];

export default function DischargePlannersPage() {
  const [instModal, setInstModal] = useState({ open: false, interest: 'pilot' });
  const openPilot = () => setInstModal({ open: true, interest: 'pilot' });

  return (
    <div className="min-h-screen bg-cn-bg text-cn-text" data-testid="discharge-planners-page">
      <Seo
        title="For Discharge Planners — Hospital Discharge Planning Software | CaraLoom"
        description="Run your discharge day from one queue: automated matching with human control, coverage checked before the first visit, orders signed in one place, and a scoreboard built on time-to-accept and time-to-first-visit."
        path="/for-discharge-planners"
        keywords={['hospital discharge planning software', 'discharge planning platform', 'home health referrals', 'post-acute placement', 'case management software', 'referral to acceptance', 'coverage verification discharge']}
        schema={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'For discharge planners', path: '/for-discharge-planners' }])]}
      />
      <PublicNavbar />

      {/* Hero — the planner's day */}
      <section className="relative overflow-hidden border-b border-cn-border">
        <div className="cn-container pt-16 md:pt-24 pb-16 md:pb-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cn-bone-200/80 dark:bg-white/5 border border-cn-border mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-cn-accent animate-pulse" aria-hidden="true" />
              <span className="text-xs font-semibold tracking-wide uppercase text-cn-text">
                Built around the discharge planner&apos;s day
              </span>
            </div>
            <h1 className="cn-display text-[2.6rem] sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight"
                data-testid="planners-hero-headline">
              Your day, minus the <span className="italic font-medium text-cn-primary dark:text-cn-accent">phone tree.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-cn-text-muted leading-relaxed max-w-2xl">
              CaraLoom runs the matching, the offers, and the paperwork on its own —
              and brings you only the moments that need a human: an exception to clear,
              a coverage hold to review, a signature to give.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button type="button" onClick={openPilot}
                className="cn-btn cn-btn-primary text-base font-bold"
                data-testid="planners-cta-pilot">
                Book a hospital pilot <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/auth?role=discharge_planner"
                className="cn-btn bg-white dark:bg-cn-ink-800 text-cn-text border border-cn-border hover:bg-cn-bone-100 dark:hover:bg-cn-ink-700 text-base"
                data-testid="planners-cta-signup">
                Start as a planner <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <p className="mt-6 font-mono text-xs tracking-wide text-cn-text-muted">
              Referral → matched → accepted → coverage checked → orders signed → first visit → watched.
            </p>
          </div>
        </div>
      </section>

      {/* The day, restructured */}
      <section className="py-20 md:py-28 bg-cn-bone-100 dark:bg-cn-ink-900 border-b border-cn-border" data-testid="planners-day">
        <div className="cn-container">
          <div className="max-w-2xl mb-12">
            <div className="cn-eyebrow mb-3">Lead with exceptions, not lists</div>
            <h2 className="cn-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
              One queue tells you what to do next.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-cn-border rounded-2xl overflow-hidden border border-cn-border">
            {DAY_TILES.map((t) => (
              <article key={t.k} className="bg-white dark:bg-cn-ink-800 p-8 md:p-10"
                data-testid={`planners-day-${t.k.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center gap-2 font-mono text-[0.72rem] uppercase tracking-[0.16em] text-cn-primary dark:text-cn-accent">
                  <t.icon className="w-4 h-4" strokeWidth={1.8} /> {t.k}
                </div>
                <h3 className="cn-display text-xl mt-3 mb-2 leading-snug">{t.title}</h3>
                <p className="text-sm text-cn-text-muted leading-relaxed">{t.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How the automation works / where humans stay in charge */}
      <section className="py-20 md:py-28" data-testid="planners-human-control">
        <div className="cn-container">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-start">
            <div>
              <div className="cn-eyebrow mb-3">How the automation works — and where humans stay in charge</div>
              <h2 className="cn-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
                The machine proposes.<br />
                <span className="text-cn-text-muted">Your people decide.</span>
              </h2>
              <p className="mt-5 text-cn-text-muted leading-relaxed max-w-lg">
                Every automated step is written to a full, auditable trace — scoring, ranking,
                offers, coverage checks, document drafting. And the loop hard-stops at the
                moments that matter, waiting for a person with a license or a badge.
              </p>
              <p className="mt-4 text-sm text-cn-text-muted leading-relaxed max-w-lg">
                Honest by design: during pilots, integrations still in test mode are labeled
                on every screen — coverage checks read
                <span className="font-mono text-xs"> “verification: simulated (pilot)”</span> until
                your payer connections go live. We don&apos;t market what isn&apos;t on.
              </p>
            </div>
            <ul className="space-y-5">
              {HUMAN_GATES.map((g) => (
                <li key={g.n} className="flex gap-4 items-start rounded-2xl border border-cn-border bg-white dark:bg-cn-ink-800 p-6"
                  data-testid={`planners-gate-${g.n}`}>
                  <div className="flex-none w-11 h-11 rounded-full bg-cn-primary/10 dark:bg-cn-accent/10 text-cn-primary dark:text-cn-accent flex items-center justify-center font-mono font-bold">
                    {g.n}
                  </div>
                  <div>
                    <h4 className="cn-display text-base font-semibold leading-snug">Human gate: {g.title}</h4>
                    <p className="text-sm text-cn-text-muted leading-relaxed mt-1">{g.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Scoreboard */}
      <section className="py-20 md:py-28 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border" data-testid="planners-scoreboard">
        <div className="cn-container">
          <div className="max-w-2xl mb-12">
            <div className="cn-eyebrow mb-3">The scoreboard</div>
            <h2 className="cn-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Two numbers your bed meetings already ask for.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-cn-border rounded-2xl overflow-hidden border border-cn-border">
            {SCOREBOARD.map((m, i) => (
              <div key={m.label} className="bg-white dark:bg-cn-ink-800 p-8 md:p-10"
                data-testid={`planners-scoreboard-${i}`}>
                <div className="flex items-center gap-2 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-cn-text-muted">
                  <Gauge className="w-4 h-4" /> {m.label}
                </div>
                <p className="text-sm text-cn-text-muted leading-relaxed mt-4">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28" data-testid="planners-final-cta">
        <div className="cn-container">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-cn-primary text-white p-10 md:p-16 text-center">
            <h2 className="cn-display text-4xl md:text-5xl !text-white leading-[1.04] tracking-tight max-w-2xl mx-auto">
              Pick one unit. Watch the queue empty itself.
            </h2>
            <p className="mt-4 text-lg text-white/80 max-w-xl mx-auto">
              Start with a single discharge team and one number — time from referral to an accepted placement.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button type="button" onClick={openPilot}
                className="cn-btn cn-btn-accent text-base font-bold"
                data-testid="planners-final-pilot">
                Book a hospital pilot <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/auth?role=discharge_planner"
                className="cn-btn bg-white text-cn-primary hover:bg-white/90 text-base font-bold"
                data-testid="planners-final-signup">
                Start as a planner <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <InstitutionInquiryModal
        open={instModal.open}
        defaultInterest={instModal.interest}
        onOpenChange={(v) => setInstModal((s) => ({ ...s, open: v }))}
      />
    </div>
  );
}
